/**
 * TonPaymentPanel Component
 * Handles TON blockchain payments for subscriptions
 * Includes wallet connection and charity transparency
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  LinearProgress
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  initTonConnect,
  connectWallet,
  disconnectWallet,
  getWalletStatus,
  paySubscription,
  formatAddress,
  getCharityBalance,
  onWalletStatusChange,
  TON_PRICES
} from '@services/tonWallet';
import { telegram } from '@services/telegram';
import { PlanId } from '@services/subscriptionService';

interface TonPaymentPanelProps {
  plan: PlanId;
  userId: string;
  onPaymentSuccess: (txHash: string) => void;
  onPaymentError: (error: string) => void;
}

export function TonPaymentPanel({ 
  plan, 
  userId, 
  onPaymentSuccess, 
  onPaymentError 
}: TonPaymentPanelProps) {
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [charityBalance, setCharityBalance] = useState<number>(0);
  const [initialized, setInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  const pricing = TON_PRICES[plan];
  const charityAmount = (pricing.price * pricing.charityPercent) / 100;

  useEffect(() => {
    // Initialize TON Connect with timeout
    const initTimeout = setTimeout(() => {
      if (!initialized) {
        console.warn('[TON] Initialization timeout - showing UI anyway');
        setInitialized(true);
        setInitError('TON wallet initialization timed out. You can try connecting manually.');
      }
    }, 5000); // 5 second timeout

    initTonConnect()
      .then(() => {
        clearTimeout(initTimeout);
        setInitialized(true);
        checkWalletStatus();
      })
      .catch((error) => {
        clearTimeout(initTimeout);
        console.error('[TON] Initialization error:', error);
        setInitialized(true); // Show UI anyway
        setInitError('Failed to initialize TON wallet. Please try again.');
      });

    // Subscribe to wallet status changes
    const unsubscribe = onWalletStatusChange((connected, address) => {
      setWalletConnected(connected);
      setWalletAddress(address);
    });

    // Load charity balance
    loadCharityBalance();

    return () => {
      clearTimeout(initTimeout);
      unsubscribe();
    };
  }, []);

  const checkWalletStatus = async () => {
    const status = await getWalletStatus();
    setWalletConnected(status.connected);
    setWalletAddress(status.address);
  };

  const loadCharityBalance = async () => {
    const { balance } = await getCharityBalance();
    setCharityBalance(balance);
  };

  const handleConnect = async () => {
    setConnecting(true);
    telegram.hapticFeedback('light');
    
    try {
      const address = await connectWallet();
      if (address) {
        setWalletConnected(true);
        setWalletAddress(address);
        telegram.hapticFeedback('success');
      }
    } catch (error) {
      console.error('Wallet connection failed:', error);
      telegram.hapticFeedback('error');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    telegram.hapticFeedback('light');
    await disconnectWallet();
    setWalletConnected(false);
    setWalletAddress(null);
  };

  const handlePayment = async () => {
    if (!walletConnected) {
      await handleConnect();
      return;
    }

    setPaying(true);
    telegram.hapticFeedback('medium');

    try {
      const result = await paySubscription(plan, userId);
      
      if (result.success && result.txHash) {
        telegram.hapticFeedback('success');
        onPaymentSuccess(result.txHash);
      } else {
        telegram.hapticFeedback('error');
        onPaymentError(result.error || 'Payment failed');
      }
    } catch (error: any) {
      telegram.hapticFeedback('error');
      onPaymentError(error.message || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  if (!initialized) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 2 }}>
        <CircularProgress size={30} sx={{ color: '#0098ea' }} />
        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
          Initializing TON wallet...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Show initialization error if any */}
      {initError && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          borderRadius: 1, 
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }}>
          <Typography variant="body2" sx={{ color: '#f87171' }}>
            ⚠️ {initError}
          </Typography>
        </Box>
      )}
      
      {/* TON Payment Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        mb: 2,
        p: 2,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(0, 152, 234, 0.1) 0%, rgba(0, 136, 204, 0.1) 100%)',
        border: '1px solid rgba(0, 152, 234, 0.3)'
      }}>
        <img 
          src="https://ton.org/download/ton_symbol.svg" 
          alt="TON" 
          style={{ width: 24, height: 24 }}
        />
        <Typography variant="subtitle1" sx={{ color: '#0098ea', fontWeight: 600 }}>
          Pay with TON
        </Typography>
        <Chip 
          label="Blockchain" 
          size="small" 
          sx={{ 
            backgroundColor: 'rgba(0, 152, 234, 0.2)',
            color: '#0098ea',
            fontSize: '0.65rem'
          }} 
        />
      </Box>

      {/* Wallet Connection Status */}
      <Card sx={{ 
        backgroundColor: 'rgba(30, 41, 59, 0.8)',
        border: walletConnected 
          ? '1px solid rgba(34, 197, 94, 0.3)' 
          : '1px solid rgba(96, 165, 250, 0.2)',
        mb: 2
      }}>
        <CardContent sx={{ p: 2 }}>
          {walletConnected ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    Wallet Connected
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>
                    {formatAddress(walletAddress || '')}
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                startIcon={<LinkOffIcon />}
                onClick={handleDisconnect}
                sx={{ color: '#ef4444', fontSize: '0.75rem' }}
              >
                Disconnect
              </Button>
            </Box>
          ) : (
            <Button
              fullWidth
              variant="outlined"
              startIcon={connecting ? <CircularProgress size={16} /> : <AccountBalanceWalletIcon />}
              onClick={handleConnect}
              disabled={connecting}
              sx={{
                borderColor: '#0098ea',
                color: '#0098ea',
                '&:hover': {
                  borderColor: '#0088cc',
                  backgroundColor: 'rgba(0, 152, 234, 0.1)'
                }
              }}
            >
              {connecting ? 'Connecting...' : 'Connect TON Wallet'}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Payment Summary */}
      <Card sx={{ 
        backgroundColor: 'rgba(30, 41, 59, 0.8)',
        border: '1px solid rgba(96, 165, 250, 0.2)',
        mb: 2
      }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 2 }}>
            Payment Summary
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              {plan === 'yearly' ? 'Yearly' : 'Monthly'} Subscription
            </Typography>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
              {pricing.price} TON
            </Typography>
          </Box>
          
          <Divider sx={{ my: 1.5, borderColor: 'rgba(96, 165, 250, 0.2)' }} />
          
          {/* Charity breakdown */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.2)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VolunteerActivismIcon sx={{ color: '#22c55e', fontSize: 18 }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#22c55e' }}>
                  Packs for Hunger
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                  {pricing.charityPercent}% of your payment
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 600 }}>
              {charityAmount.toFixed(2)} TON
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Charity Impact Banner */}
      <Alert 
        severity="info" 
        icon={<VolunteerActivismIcon />}
        sx={{ 
          mb: 2,
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          '& .MuiAlert-icon': { color: '#22c55e' },
          '& .MuiAlert-message': { color: '#94a3b8' }
        }}
        action={
          <Button
            size="small"
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            href="https://packsforhunger.org"
            target="_blank"
            sx={{ color: '#22c55e', fontSize: '0.7rem' }}
          >
            Learn More
          </Button>
        }
      >
        <Typography variant="caption">
          {plan === 'yearly' 
            ? '🎉 Annual subscribers donate 20% to charity - double the impact!' 
            : 'Your subscription helps feed children in New Mexico'
          }
        </Typography>
        {charityBalance > 0 && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#22c55e' }}>
            Total donated: {charityBalance.toFixed(2)} TON (ziagivesback.ton)
          </Typography>
        )}
      </Alert>

      {/* Pay Button */}
      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={handlePayment}
        disabled={paying}
        sx={{
          background: 'linear-gradient(135deg, #0098ea 0%, #0088cc 100%)',
          color: 'white',
          py: 1.5,
          fontWeight: 600,
          '&:hover': {
            background: 'linear-gradient(135deg, #0088cc 0%, #007acc 100%)'
          },
          '&:disabled': {
            background: 'rgba(0, 152, 234, 0.3)',
            color: 'rgba(255, 255, 255, 0.5)'
          }
        }}
      >
        {paying ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} sx={{ color: 'white' }} />
            Processing...
          </Box>
        ) : walletConnected ? (
          `Pay ${pricing.price} TON`
        ) : (
          'Connect Wallet to Pay'
        )}
      </Button>

      {paying && (
        <LinearProgress 
          sx={{ 
            mt: 1, 
            borderRadius: 1,
            backgroundColor: 'rgba(0, 152, 234, 0.2)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#0098ea'
            }
          }} 
        />
      )}

      {/* Security note */}
      <Typography 
        variant="caption" 
        sx={{ 
          display: 'block', 
          textAlign: 'center', 
          mt: 2, 
          color: '#64748b' 
        }}
      >
        🔒 Secure blockchain payment · All transactions verified on TON
      </Typography>
    </Box>
  );
}

export default TonPaymentPanel;
